import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { useWsEvent } from '@/auth/WsProvider';

/**
 * Модалка прогресса пакетного удаления начислений.
 *
 * Зеркальна `MainChargesSyncModal` (выгрузка в 1С) — слушает события
 * `sync.accrual-delete.event` с тем же jobId, что вернул PHP. Закрытие
 * окна не останавливает фоновый job: пользователь может закрыть и
 * вернуться позже — итог увидит на странице (строки пропадут).
 */
function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₽';
}
function fmtTime(d) {
  return d.toTimeString().slice(0, 8);
}

export default function MainChargesDeleteModal({ jobId, total: totalProp, onClose, onDone }) {
  const [events, setEvents]   = useState([]);
  const [total,  setTotal]    = useState(totalProp ?? null);
  const [done,   setDone]     = useState(false);
  const [summary, setSummary] = useState(null);
  const listRef = useRef(null);

  // Авто-скролл вниз при новом событии
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [events.length]);

  useWsEvent('sync.accrual-delete.event', (msg) => {
    if (!msg || msg.jobId !== jobId) return;
    if (msg.kind === 'start') {
      setTotal(msg.total ?? null);
      setEvents((prev) => [...prev, { ...msg, time: new Date() }]);
      return;
    }
    if (msg.kind === 'summary') {
      setSummary({
        ok:      msg.okCount      || 0,
        err:     msg.errCount     || 0,
        skipped: msg.skippedCount || 0,
      });
      return;
    }
    if (msg.kind === 'done') {
      setDone(true);
      onDone && onDone();
      return;
    }
    setEvents((prev) => [...prev, { ...msg, time: new Date() }]);
  });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const counts = events.reduce((acc, e) => {
    if (e.kind === 'ok')       acc.ok++;
    if (e.kind === 'error')    acc.err++;
    if (e.kind === 'skip')     acc.skip++;
    if (e.kind === 'progress') acc.progress++;
    return acc;
  }, { ok: 0, err: 0, skip: 0, progress: 0 });

  const processed = counts.ok + counts.err + counts.skip;
  const percent = total ? Math.round((processed / total) * 100) : 0;

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>Удаление начислений</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <div className="sd-edit__body" style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
          <div className="sync-progress">
            <div className="sync-progress__bar">
              <div className="sync-progress__fill" style={{ width: percent + '%' }} />
            </div>
            <div className="sync-progress__text">
              {total !== null ? (
                <>
                  Обработано {processed} из {total} ({percent}%)
                  {' · '}<span className="sync-progress__ok">✓ {counts.ok}</span>
                  {counts.err  > 0 && <> · <span className="sync-progress__err">✕ {counts.err}</span></>}
                  {counts.skip > 0 && <> · <span className="sync-progress__skip">↷ {counts.skip}</span></>}
                </>
              ) : (
                'Подключаемся к Node-сервису…'
              )}
            </div>
          </div>

          <div className="sync-log" ref={listRef}>
            {events.length === 0 && (
              <div className="ref-empty">Ожидаем события от Node-сервиса…</div>
            )}
            {events.map((e, i) => (
              <DeleteRow key={i} event={e} />
            ))}
          </div>

          {done && summary && (
            <div className="sync-summary">
              <Icon name="check" size={14} />
              <span>
                Готово. Удалено: <b>{summary.ok}</b>
                {summary.err     > 0 && <>, ошибок: <b style={{ color: 'var(--red-fg)' }}>{summary.err}</b></>}
                {summary.skipped > 0 && <>, пропущено: <b>{summary.skipped}</b></>}
              </span>
            </div>
          )}
          {!done && (
            <div className="muted" style={{ fontSize: 11.5 }}>
              Закрытие окна <b>не останавливает</b> удаление — оно идёт в фоне на сервере.
              Вернётесь — увидите итог на странице.
            </div>
          )}
        </div>

        <div className="sd-edit__foot">
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose}>
            {done ? 'Закрыть' : 'Скрыть окно'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteRow({ event: e }) {
  const time = e.time ? fmtTime(e.time) : '';
  const cls  = 'sync-row sync-row--' + e.kind;
  let icon = 'doc';
  if (e.kind === 'ok')       icon = 'check';
  if (e.kind === 'error')    icon = 'alert';
  if (e.kind === 'skip')     icon = 'eye-off';
  if (e.kind === 'progress') icon = 'clock';
  if (e.kind === 'start')    icon = 'spark';
  if (e.kind === 'fatal')    icon = 'alert';
  return (
    <div className={cls}>
      <span className="sync-row__time mono">{time}</span>
      <span className="sync-row__icon"><Icon name={icon} size={11} /></span>
      {e.kind === 'start' ? (
        <span className="sync-row__msg">Старт. Всего к обработке: {e.total}</span>
      ) : (
        <>
          {e.studentName && <span className="sync-row__name">{e.studentName}</span>}
          {e.agreementNumber && (
            <span className="muted" style={{ fontSize: 11 }}>· №{e.agreementNumber}</span>
          )}
          {Number(e.amount) > 0 && (
            <span className="muted mono" style={{ fontSize: 11 }}>· {fmtMoney(e.amount)}</span>
          )}
          <span className="sync-row__msg">{e.message}</span>
        </>
      )}
    </div>
  );
}
